// Файл Input.js

/**
 * Общий Input выбирает режим отображения по переданным параметрам.
 * Обычные поля рендерятся нативным input или textarea.
 * Числовой режим нормализует значение и форматирует разряды.
 * При наличии tags управление передаётся компоненту TaggedInput.
 * Благодаря этому единый API поля сохраняется для разных сценариев.
 */

import { forwardRef, useState } from 'react';

import TaggedInput from './TaggedInput';

import './Input.css';



const GROUP_SEPARATOR = ' ';


export const normalize_number_value = (value) => {
    const compact = String(value ?? '')
        .replace(/[\s\u00A0\u202F]/g, '')
        .replace(',', '.');
    const sign = compact.startsWith('-') ? '-' : '';
    const unsigned = compact.replace(/-/g, '');
    const parts = unsigned.split('.');
    const integer = parts.shift() || '';
    const clean_integer = integer.replace(/\D/g, '');
    const clean_decimal = parts
        .join('')
        .replace(/\D/g, '');
    const has_decimal_separator = unsigned.includes('.');

    if (!clean_integer && !clean_decimal) {
        return sign && compact === '-' ? '-' : '';
    }

    const normalized_integer = clean_integer || '0';

    if (!has_decimal_separator) {
        return `${sign}${normalized_integer}`;
    }

    return (
        `${sign}${normalized_integer}.`
        + clean_decimal
    );
};


export const format_number_value = (value) => {
    const normalized = normalize_number_value(value);

    if (!normalized || normalized === '-') {
        return normalized;
    }

    const sign = normalized.startsWith('-') ? '-' : '';
    const unsigned = sign
        ? normalized.slice(1)
        : normalized;
    const [integer, decimal] = unsigned.split('.');
    const grouped_integer = integer.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        GROUP_SEPARATOR,
    );

    if (!unsigned.includes('.')) {
        return `${sign}${grouped_integer}`;
    }

    return (
        `${sign}${grouped_integer},`
        + `${decimal ?? ''}`
    );
};


const numeric_event = (event, value) => ({
    ...event,
    currentTarget: {
        value,
    },
    target: {
        value,
    },
});


const NumberInput = ({
    className = '',
    onBlur,
    onChange,
    onFocus,
    value,
    ...props
}) => {
    const normalized_value = normalize_number_value(value);
    const [
        editing_value,
        set_editing_value,
    ] = useState(null);
    const is_editing = editing_value !== null;

    const handle_change = (event) => {
        const raw_value = normalize_number_value(
            event.target.value,
        );
        set_editing_value(raw_value);
        onChange?.(
            numeric_event(event, raw_value),
        );
    };

    const handle_focus = (event) => {
        set_editing_value(normalized_value);
        onFocus?.(event);
    };

    const handle_blur = (event) => {
        const raw_value = normalize_number_value(
            editing_value
            ?? event.target.value,
        );
        set_editing_value(null);
        onBlur?.(
            numeric_event(event, raw_value),
        );
    };

    return (
        <input
            {...props}
            type="text"
            inputMode="decimal"
            data-input-type="number"
            value={format_number_value(
                is_editing
                    ? editing_value
                    : value,
            )}
            onChange={handle_change}
            onFocus={handle_focus}
            onBlur={handle_blur}
            className={[
                'app-input',
                className,
            ].filter(Boolean).join(' ')}
        />
    );
};


const Input = forwardRef(({
    className = '',
    multiline = false,
    native_number = false,
    tags = null,
    type = 'text',
    ...props
}, ref) => {
    if (
        Array.isArray(tags)
        && tags.length > 0
        && type !== 'number'
    ) {
        return (
            <TaggedInput
                {...props}
                className={className}
                multiline={multiline}
                tags={tags}
            />
        );
    }

    if (multiline) {
        return (
            <textarea
                {...props}
                ref={ref}
                className={[
                    'app-input',
                    className,
                ].filter(Boolean).join(' ')}
            />
        );
    }

    if (type === 'number' && !native_number) {
        return (
            <NumberInput
                {...props}
                className={className}
            />
        );
    }

    return (
        <input
            {...props}
            ref={ref}
            type={type}
            className={[
                'app-input',
                className,
            ].filter(Boolean).join(' ')}
        />
    );
});


export default Input;
